import { type RefObject, useEffect, useRef } from 'react'
import { cn } from '@/helpers/utils'

interface TabsProps<T extends string> {
	className?: string
	options: {
		label: string
		value: T
	}[]
	activeTab: T
	setActiveTab: (tab: T) => void
	scrollContainerRef?: RefObject<HTMLDivElement | null>
}

function Tabs<T extends string>({
	className,
	options,
	activeTab,
	setActiveTab,
	scrollContainerRef
}: TabsProps<T>) {
	const tabRefs = useRef<Map<T, HTMLButtonElement>>(new Map())
	const hasScrolledRef = useRef(false)

	useEffect(() => {
		if (!scrollContainerRef?.current || hasScrolledRef.current) {
			return
		}

		const activeTabElement = tabRefs.current.get(activeTab)
		if (!activeTabElement) {
			return
		}

		const scrollContainer = scrollContainerRef.current

		const tabCenter =
			activeTabElement.offsetLeft + activeTabElement.offsetWidth / 2
		const scrollLeft = Math.max(
			0,
			tabCenter - scrollContainer.clientWidth / 2
		)
		scrollContainer.scrollLeft = scrollLeft

		hasScrolledRef.current = true
	}, [activeTab, scrollContainerRef])

	return (
		<div className={cn('flex gap-2', className)}>
			{options.map(({ label, value }) => (
				<button
					className={cn(
						'min-w-14 shrink-0 cursor-pointer whitespace-nowrap rounded-5xl bg-grey-200 px-4 py-3 font-medium text-grey-1000 text-xs/4',
						activeTab === value && 'bg-grey-1000 text-neutral-white'
					)}
					key={value}
					onClick={() => setActiveTab(value)}
					ref={(el) => {
						if (el) {
							tabRefs.current.set(value, el)
						} else {
							tabRefs.current.delete(value)
						}
					}}
					type='button'
				>
					{label}
				</button>
			))}
		</div>
	)
}

Tabs.displayName = 'Tabs'
export { Tabs }
